import React from "react";
import Sparkline from "./Sparkline";
import PillDelta from "./PillDelta";
import useCountUp from "../lib/useCountUp";

/**
 * KpiCard — a modern summary card with:
 *  - eyebrow label
 *  - large value (animated count-up when countTarget prop is set)
 *  - delta pill (rounded, tinted background)
 *  - sparkline trend at the bottom
 *  - hover lift (translateY + shadow bump)
 */
export const KpiCard = ({
  label,
  value,
  countTarget,
  countFormatter,
  countDecimals = 0,
  delta,
  hint,
  footer,
  spark,
  sparkColor,
  tone = "default",
  testId,
}) => {
  // If countTarget is passed, animate a numeric count-up and format via provided fn.
  const animatedNumeric = useCountUp(countTarget ?? 0, {
    duration: 700,
    decimals: countDecimals,
    startOnMount: countTarget != null,
  });
  const displayValue = countTarget != null
    ? (countFormatter ? countFormatter(animatedNumeric) : animatedNumeric)
    : value;

  const toneColor =
    tone === "warn" ? "#F5A623"
    : tone === "danger" ? "#F04438"
    : tone === "success" ? "#12B76A"
    : "#00B4D8";

  const sparkStroke = sparkColor || (
    delta == null ? toneColor
    : delta >= 0 ? "#12B76A"
    : "#F04438"
  );

  return (
    <div
      data-testid={testId}
      className="ang-panel ang-lift p-5 flex flex-col justify-between min-h-[148px] relative overflow-hidden"
    >
      {/* Corner accent bar */}
      <div className="absolute top-0 left-5 right-5 h-[3px] rounded-b-full opacity-70"
        style={{ background: `linear-gradient(90deg, ${toneColor}, transparent 80%)` }}
      />
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-bold uppercase tracking-widest text-ang-muted">
          {label}
        </div>
        {delta != null && (
          <PillDelta value={delta} />
        )}
      </div>
      <div className="mt-3">
        <div className="text-[26px] leading-none font-extrabold tabular-nums text-ang" style={{ letterSpacing: "-0.02em" }}>
          {displayValue}
        </div>
        {hint && <div className="mt-1.5 text-[12px] text-ang-muted">{hint}</div>}
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          {spark && spark.length > 1 && (
            <Sparkline data={spark} color={sparkStroke} height={28} width={140} />
          )}
        </div>
        {footer && <div className="text-[10px] uppercase tracking-widest text-ang-faint shrink-0">{footer}</div>}
      </div>
    </div>
  );
};

export default KpiCard;
